import React, { ReactNode, useRef } from 'react';
import { Button, Divider } from 'src/components/atoms';
import { useOutsideClick } from 'src/hooks';
import {
  DividerContainer,
  PageCover,
  SlideOutBody,
  SlideOutFooter,
  SlideOutHeader,
  SlideOutMainHeader,
  SlideOutStyled,
  SlideOutSubTitle,
  SlideOutTitle,
  StyledCloseIcon,
} from './SlideOut.styles';

interface Props {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children?: React.ReactNode;
  cancelButtonText?: string;
  cancelButtonDataTestid?: string;
  confirmButtonText: string;
  confirmButtonDataTestid: string;
  onCancel?: () => void;
  onConfirm: () => void;
  applyButtonDisabled: boolean;
  subtitle?: string | ReactNode;
  bodyStyle?: React.CSSProperties;
  showFooter?: boolean;
  dividerHasPadding?: boolean;
  className?: string;
  containerDataTestid?: string;
  $width?: string;
  headerChildren?: ReactNode;
  bodyRevealDelayMs?: number;
  headerRevealDelayMs?: number;
}

export const SlideOut = ({
  isOpen,
  title,
  onClose,
  onCancel,
  onConfirm,
  children,
  cancelButtonText,
  cancelButtonDataTestid,
  confirmButtonText,
  confirmButtonDataTestid,
  applyButtonDisabled,
  subtitle,
  bodyStyle,
  showFooter = true,
  dividerHasPadding = false,
  className,
  containerDataTestid,
  headerChildren,
  ...props
}: Props) => {
  const slideOutRef = useRef<HTMLDivElement>(null);

  useOutsideClick(slideOutRef, () => {
    if (isOpen) {
      onClose();
    }
  });

  return (
    <>
      <PageCover isOpen={isOpen} />
      <SlideOutStyled
        isOpen={isOpen}
        ref={slideOutRef}
        className={className}
        data-testid={containerDataTestid}
        {...props}
      >
        <SlideOutHeader
          isOpen={isOpen}
          $width={props.$width}
          $headerRevealDelayMs={props.headerRevealDelayMs}
        >
          <SlideOutMainHeader>
            <StyledCloseIcon
              className="uil uil-multiply"
              onClick={onClose}
              size="16px"
            />
            <SlideOutTitle>{title}</SlideOutTitle>
            {subtitle && <SlideOutSubTitle>{subtitle}</SlideOutSubTitle>}
          </SlideOutMainHeader>
          {headerChildren}
        </SlideOutHeader>
        <SlideOutBody
          style={bodyStyle}
          isOpen={isOpen}
          $width={props.$width}
          $bodyRevealDelayMs={props.bodyRevealDelayMs}
        >
          {children}
        </SlideOutBody>
        {showFooter && !dividerHasPadding && <Divider variant="horizontal" />}
        {showFooter && dividerHasPadding && (
          <DividerContainer>
            <Divider variant="horizontal" />
          </DividerContainer>
        )}
        {showFooter && (
          <SlideOutFooter hasOneButton={!cancelButtonText} isOpen={isOpen}>
            {cancelButtonText && (
              <Button
                variant="secondary"
                size="medium"
                onClick={onCancel}
                data-testid={cancelButtonDataTestid}
              >
                {cancelButtonText}
              </Button>
            )}
            <Button
              variant="primary"
              size="medium"
              onClick={onConfirm}
              disabled={applyButtonDisabled}
              data-testid={confirmButtonDataTestid}
            >
              {confirmButtonText}
            </Button>
          </SlideOutFooter>
        )}
      </SlideOutStyled>
    </>
  );
};
